<template>
  <div class="iframe-container" ref="containerRef">
    <iframe
      ref="iframeRef"
      :src="iframeSrc"
      :title="title"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
      frameborder="0"
      @load="handleLoad"
      @error="handleError"
    ></iframe>

    <div v-if="loading" class="loading-overlay">
      <slot name="loading">
        <div class="default-loading">
          <a-spin />
          <span>正在加载...</span>
        </div>
      </slot>
    </div>

    <div v-if="error" class="error-overlay">
      <slot name="error" :errorMessage="errorMessage" @retry="initIframe">
        <ErrorDisplay @retry="initIframe" />
      </slot>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed, watchEffect, defineProps } from 'vue'
import { useStore } from 'vuex'
import { getAdminUser } from '@/api/login'
import ErrorDisplay from './ErrorDisplay.vue'

const props = defineProps({
  origin: {
    type: String,
    default: null
  },
  ucPatientId: {
    type: String,
    default: null
  },
  workOrderId: {
    type: String,
    default: null
  }
})

const store = useStore()
const curWorkOrderInfo = computed(() => store.state.workOrder.workOrderGroupInfo.targetWorkOrder)
const followUpOrderFlag = computed(() => curWorkOrderInfo.value?.followUpOrderFlag) // 随访计划展示页标识 true-随访计划 false-患者档案
const curWorkOrderDetails = computed(
  () => store.state.workOrder.workOrderDetails.get(props.workOrderId)?.workOrder
)
const isWorkDetails = computed(() => props.origin === 'orderDetails') // 来源是否为工单详情
const patientId = computed(() =>
  isWorkDetails.value ? curWorkOrderDetails.value.ucPatientId : curWorkOrderInfo.value?.ucPatientId
)
const patientPlanId = computed(() =>
  isWorkDetails.value
    ? curWorkOrderDetails.value.patientPlanId
    : curWorkOrderInfo.value?.patientPlanId
)
const orgId = computed(() =>
  isWorkDetails.value ? curWorkOrderDetails.value.orgId : curWorkOrderInfo.value?.orgId
)
const followupPlanId = computed(() =>
  isWorkDetails.value
    ? curWorkOrderDetails.value.followupPlanId
    : curWorkOrderInfo.value?.followupPlanId
)
const iframeSrc = ref('')
const src = ref('')
const iframeRef = ref(null)
const title = '院内随访'
const errorMessage = '加载失败，请刷新重试'
const loading = ref(false)
const error = ref(false)
const accountInfo = ref(null)

watch(
  () => curWorkOrderInfo.value,
  () => {
    initIframe()
  },
  { deep: true }
)

watchEffect(() => {
  src.value =
    followUpOrderFlag.value && props.origin !== 'orderDetails'
      ? process.env.VUE_APP_FOLLOW_UP_DETAILS
      : process.env.VUE_APP_FOLLOW_UP_PATIENT
})

// 初始化 iframe（获取 token 并设置 src）
const initIframe = async () => {
  loading.value = true
  error.value = false
  iframeSrc.value = ''
  accountInfo.value = null
  try {
    accountInfo.value = await getAccount()
    handleLoad()
  } catch (err) {
    console.log('获取账号密码失败')
    error.value = true
  }
}

const handleLoad = () => {
  // iframe 加载完成后发送 token
  if (iframeRef.value && accountInfo.value) {
    const srcValue =
      followUpOrderFlag.value && props.origin !== 'orderDetails'
        ? src.value
        : `${src.value}/${patientId.value}`
    const commonParams = `menuHide=true&orgId=${orgId.value}&account=${accountInfo.value?.userName}&password=${accountInfo.value?.password}`
    const diffParams =
      followUpOrderFlag.value && props.origin !== 'orderDetails'
        ? `businessIds=${curWorkOrderInfo.value.patientPlanDetailId}&patientPlanId=${patientPlanId.value}&followupPlanId=${followupPlanId.value}`
        : `patientId=${patientId.value}&patientPlanId=${patientPlanId.value}&origin=${props.origin}`
    iframeSrc.value = `${srcValue}?${commonParams}&${diffParams}`
  }
  loading.value = false
}

const handleError = () => {
  loading.value = false
  error.value = true
}

const getAccount = async () => {
  try {
    if (!orgId.value) return
    const res = await getAdminUser({ orgId: orgId.value })
    return res || {}
  } catch (err) {
    error.value = true
  }
}

onMounted(() => {
  initIframe()
})
</script>

<style scoped lang="scss">
.iframe-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-right: 1px solid #f0f0f0;

  iframe {
    margin: 0;
    padding: 0;
    border: none;
    width: 100%;
    height: 100%;
  }

  .loading-overlay,
  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f6fa;
    z-index: 10;
  }
}
</style>
